/**
 * @file useBinsPage.ts
 * @description Данные и операции справочника ячеек (phase19.14, W14).
 *
 * Держит выбранный склад, список ячеек и три операции (создать, архивировать,
 * восстановить). Вынесено из страницы: там остаётся раскладка, диалоги и
 * sticky action-bar (docs/processes/engineering-principles.md, SRP).
 *
 * Архивация непустой ячейки отклоняется сервером (409 `bin_not_empty`) —
 * текст показываем как есть, он уже объясняет, что делать.
 */

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { useWmsConfirm } from "../use-wms-confirm";
import { useNotify } from "../../ui/NotifyProvider";
import { binsApi, type StorageBinDto } from "../../../lib/api-bins";
import { warehousesApi, type WarehouseDto } from "../../../lib/api-wms";
import { WAREHOUSE_KIND_META } from "../../../lib/wms-warehouse-kind";
import { wmsErrorText } from "../../../lib/wms-error-text";

export function useBinsPage() {
  const notify = useNotify();
  const asker = useWmsConfirm();
  const [searchParams] = useSearchParams();
  // `?wh=` — вход со строки «Требует внимания» дашборда: она указывает на
  // склад, где партии лежат без адреса, а не на склад по умолчанию.
  const requestedWarehouseId = searchParams.get("wh") ?? "";
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);
  const [warehouseId, setWarehouseId] = useState<string>(requestedWarehouseId);
  const [bins, setBins] = useState<StorageBinDto[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    warehousesApi
      .list()
      .then((list) => {
        setWarehouses(list);
        // Склад из адреса выигрывает у склада по умолчанию; несуществующий
        // (ссылка устарела) откатываем на умолчание, а не показываем пустоту.
        if (list.some((w) => w.id === requestedWarehouseId)) return;
        const first = list.find((w) => w.isDefault) ?? list[0];
        if (first) setWarehouseId(first.id);
      })
      .catch(() => {
        // Без справочника складов выбор склада пуст, а список ячеек не
        // грузится вовсе — молчать об этом нельзя.
        setWarehouses([]);
        notify.error("Не удалось загрузить склады, фильтр недоступен");
      });
  }, [notify, requestedWarehouseId]);

  const refresh = useCallback(async () => {
    if (!warehouseId) {
      setBins([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setBins(await binsApi.list({ warehouseId, includeArchived }));
      setError(null);
    } catch (err) {
      setError(wmsErrorText(err, "Не удалось загрузить ячейки"));
    } finally {
      setLoading(false);
    }
  }, [warehouseId, includeArchived]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const warehouse = warehouses.find((w) => w.id === warehouseId) ?? null;
  // Виду склада «без полок» (например, витрине) адресное хранение не положено.
  const supportsBins = warehouse
    ? WAREHOUSE_KIND_META[warehouse.kind]?.supportsBins !== false
    : true;

  /** Создать одну ячейку; ошибку оставляем inline в диалоге. */
  const create = useCallback(
    async (input: { code: string; label?: string }): Promise<boolean> => {
      if (!warehouseId) return false;
      setSaving(true);
      setCreateError(null);
      try {
        await binsApi.create({ warehouseId, ...input });
        notify.success(`Ячейка ${input.code} создана`);
        await refresh();
        return true;
      } catch (err) {
        setCreateError(wmsErrorText(err, "Не удалось создать ячейку"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [warehouseId, notify, refresh],
  );

  const archive = useCallback(
    async (bin: StorageBinDto) => {
      // Окно кабинета вместо системного `confirm()` (phase19.31).
      const ok = await asker.confirm({
        title: `Архивировать ячейку ${bin.code}?`,
        description: "Код больше не переиспользуется.",
        action: "В архив",
        danger: true,
      });
      if (!ok) return;
      try {
        await binsApi.archive(bin.id);
        notify.success(`Ячейка ${bin.code} в архиве`);
        await refresh();
      } catch (err) {
        notify.error(wmsErrorText(err, "Не удалось архивировать ячейку"));
      }
    },
    [notify, refresh, asker],
  );

  const restore = useCallback(
    async (bin: StorageBinDto) => {
      try {
        await binsApi.update(bin.id, { isActive: true });
        notify.success(`Ячейка ${bin.code} восстановлена`);
        await refresh();
      } catch (err) {
        notify.error(wmsErrorText(err, "Не удалось восстановить ячейку"));
      }
    },
    [notify, refresh],
  );

  return {
    warehouses,
    warehouse,
    warehouseId,
    setWarehouseId,
    supportsBins,
    bins,
    includeArchived,
    setIncludeArchived,
    loading,
    error,
    saving,
    createError,
    setCreateError,
    refresh,
    create,
    archive,
    restore,
    /** Вопрос «Архивировать ячейку?» — рисует страница (`WmsConfirmDialog`). */
    confirmRequest: asker.request,
    settleConfirm: asker.settle,
  };
}
